<script>
import PhoenixForm from '@phoenix/mixins/components/form/PhoenixForm';
import { PhxIcon } from '@phx-styleguides/als';
import { useForm } from 'vee-validate';
import { defineAsyncComponent } from 'vue';
import { FEATURE_FLAG, getFeatureFlag } from '@phoenix/helpers/feature-flag-helper';

export default {
  components: {
    ChinesePIPL: defineAsyncComponent(() => import('@maison/components/form/fields/ChinesePIPL')),
    KoreanGDPR: defineAsyncComponent(() => import('@maison/components/form/KoreanGDPR')),
    PhxIcon,
  },

  mixins: [PhoenixForm],

  provide() {
    return {
      formType: this.formType,
    };
  },

  props: {
    customSubmitButtonClasses: {
      type: String,
      default: null,
    },
    centeredActions: {
      type: Boolean,
      default: false,
    },
    hasFormActionsMarginTop: {
      type: Boolean,
      default: true,
    },
    overrideFormActionClasses: {
      type: String,
      default: '',
    },
  },

  setup() {
    const { setValues, validate } = useForm();
    return { setValues, validate };
  },

  computed: {
    shouldDisplayNewsletter() {
      return !this.shouldDisplayKoreanGDPR && getFeatureFlag(FEATURE_FLAG.SWSE_NEWSLETTER);
    },

    submitButtonClasses() {
      return this.customSubmitButtonClasses || 'btn';
    },

    termsLinkClasses() {
      return ['form-actions__terms'];
    },

    cancelClass() {
      return ['btn btn--secondary', { 'btn--disabled': this.isDisabled }];
    },

    formActionsClasses() {
      if (this.overrideFormActionClasses) {
        return this.overrideFormActionClasses;
      }

      return [
        'form-actions',
        {
          'form-actions--start': !this.centeredActions,
          'mt-md': this.hasFormActionsMarginTop,
        },
      ];
    },

    requiredFieldsText() {
      return this.$t('Form.required_field');
    },
  },

  methods: { getFeatureFlag },
};
</script>

<template>
  <form ref="form" style="position: relative" @submit.prevent="submit">
    <div v-if="shouldDisplayRequiredText" class="divider-wrapper">
      <hr class="divider divider--md md:divider--lg" />
      {{ requiredFieldsText }}<abbr class="text-ruby">*</abbr>
    </div>

    <div v-if="isLoading" class="flex flex-grid--center">
      <div class="loader-overlay loader-overlay--light">
        <div class="loader loader--dark loader--lg">
          <div class="loader__inner"></div>
        </div>
      </div>
    </div>

    <slot :should-display-newsletter="shouldDisplayNewsletter" />

    <korean-g-d-p-r v-if="shouldDisplayKoreanGDPR" :form-type="koreanFormType" @change="onKoreanGDPRChange" />

    <chinese-p-i-p-l
      v-if="shouldDisplayPIPLConsentCheckboxes"
      :value="chinesePiplConsents"
      :custom-inner-class="customConsentInnerClass"
      :custom-outer-class="customConsentOuterClass"
      :form-type="formType"
      @on-chinese-pipl-consent-change="onChinesePiplConsentChange"
    />

    <div v-if="!showErrorsInToast && mainErrors" class="alert alert--error mt-base" role="alert">
      {{ mainErrors }}
    </div>

    <div v-if="shouldShowFormActions" :class="formActionsClasses">
      <slot
        name="form-actions"
        :is-disabled="isDisabled"
        :submit-button-classes="submitButtonClasses"
        :submit-text="submitText"
      />

      <button
        v-if="shouldDisplaySubmitButton"
        ref="submitButton"
        :disabled="isDisabled"
        :class="submitButtonClasses"
        type="submit"
        data-cy="form-submit-btn"
      >
        <span class="btn__text">{{ submitText }}</span>
      </button>

      <button
        v-if="shouldDisplayPreviousButton"
        class="btn btn--default btn--negative"
        type="button"
        @click.prevent="goToPreviousStep"
      >
        <span class="btn__text">{{ previousStepText }}</span>
      </button>

      <button
        v-if="shouldDisplayCancelButton"
        ref="cancelButton"
        :disabled="isDisabled"
        :class="cancelClass"
        data-cy="form-cancel-btn"
        @click.prevent="onCancelCallback"
      >
        <span class="btn__text">{{ cancelText }}</span>
      </button>

      <div v-if="shouldDisplayTermsLink" :class="termsLinkClasses">
        <div class="flex gap-sm items-center">
          <phx-icon icon="info" style="min-width: 20px" />
          <p v-html="termsLink" />
        </div>
      </div>
    </div>
  </form>
</template>
